import { toJsonLdString } from "./jsonld"

const REVIEW_PARENT_TYPES = [
  "Product",
  "Organization",
  "Service",
  "Person",
  "LocalBusiness",
]

function schemaTypes(node: unknown): string[] {
  if (typeof node !== "object" || node === null || !("@type" in node)) {
    return []
  }

  const type = node["@type" as keyof typeof node]

  if (Array.isArray(type)) {
    return type.filter((value): value is string => typeof value === "string")
  }

  return typeof type === "string" ? [type] : []
}

function hasSchemaType(node: unknown, type: string): boolean {
  return schemaTypes(node).includes(type)
}

function sanitizeSchemaNode(node: unknown, parent: unknown = null, key = "") {
  if (Array.isArray(node)) {
    node.forEach((child) => sanitizeSchemaNode(child, parent, key))

    return
  }

  if (typeof node !== "object" || node === null) {
    return
  }

  const record = node as Record<string, unknown>
  const parentTypes = schemaTypes(parent)
  const isNestedReview =
    key === "review" &&
    hasSchemaType(record, "Review") &&
    parentTypes.some((type) => REVIEW_PARENT_TYPES.includes(type))
  const isNestedAggregateRating =
    key === "aggregateRating" &&
    hasSchemaType(record, "AggregateRating") &&
    parentTypes.some((type) => REVIEW_PARENT_TYPES.includes(type))

  if (isNestedReview || isNestedAggregateRating) {
    delete record.itemReviewed
  }

  if (
    key === "publisher" &&
    hasSchemaType(record, "Organization") &&
    !record.name
  ) {
    record.name = "CitizenShipper"
  }

  Object.entries(record).forEach(([childKey, value]) => {
    sanitizeSchemaNode(value, record, childKey)
  })
}

/**
 * Normalizes a CMS-authored JSON-LD block for a <script> tag, or returns null
 * when the content cannot be trusted. Re-serializing through JSON.stringify
 * replaces whatever escaping the author used with standard JSON escapes, which
 * Googlebot now requires — it applies only a single pass of HTML unescaping, so
 * a double-escaped entity would otherwise reach search as literal text.
 */
export function sanitizeStoryblokRichSnippetContent(
  content: string,
  onParseFailure?: (error: unknown) => void,
): string | null {
  let schema: unknown

  try {
    schema = JSON.parse(content)
  } catch (error) {
    onParseFailure?.(error)

    return null
  }

  sanitizeSchemaNode(schema)

  return toJsonLdString(schema)
}
